import type { AiRankedRetrievalRecord, AiRetrievalRecord, AiRetrievalRequest } from './retrievalTypes'

const tokenize = (value: string) =>
  new Set(value.toLowerCase().split(/[^a-z0-9]+/).filter((token) => token.length >= 3))

const selectedIds = (request: AiRetrievalRequest) =>
  new Set((request.selectedTargets ?? []).flatMap((target) => [target.id, target.parentId].filter((id): id is string => Boolean(id))))

const daysUntil = (dateIso: string | null | undefined, nowIso: string) => {
  if (!dateIso) return null
  const time = new Date(dateIso).getTime()
  const now = new Date(nowIso).getTime()
  if (!Number.isFinite(time) || !Number.isFinite(now)) return null
  return Math.ceil((time - now) / 86_400_000)
}

const scoreText = (queryTokens: Set<string>, record: AiRetrievalRecord) => {
  const recordTokens = tokenize(`${record.title} ${record.summary} ${record.status ?? ''} ${record.workflowStatus ?? ''}`)
  let score = 0
  queryTokens.forEach((token) => {
    if (recordTokens.has(token)) score += 7
  })
  return score
}

const addFactor = (
  factors: AiRankedRetrievalRecord['ranking']['factors'],
  name: string,
  score: number,
  reason: string,
) => {
  if (score <= 0) return
  factors.push({ name, score, reason })
}

export const rankRetrievalRecord = (
  record: AiRetrievalRecord,
  request: AiRetrievalRequest,
): AiRankedRetrievalRecord => {
  const factors: AiRankedRetrievalRecord['ranking']['factors'] = []
  const queryTokens = tokenize(request.query)
  const selected = selectedIds(request)
  const nowIso = request.nowIso ?? new Date().toISOString()
  const dependencyIds = new Set([...(record.dependencyIds ?? []), ...(record.blockerIds ?? []), ...(record.linkedRecordIds ?? [])])

  addFactor(factors, 'query_match', scoreText(queryTokens, record), 'matches request language')
  addFactor(factors, 'direct_target', selected.has(record.id) || (record.parentId ? selected.has(record.parentId) : false) ? 60 : 0, 'directly selected scope')
  addFactor(factors, 'dependency_proximity', [...selected].some((id) => dependencyIds.has(id)) ? 34 : 0, 'near selected dependency chain')
  addFactor(factors, 'same_board', request.activeBoard && record.board === request.activeBoard ? 8 : 0, 'same active board')
  addFactor(factors, 'blocked', record.isBlocked ? 20 : 0, 'blocked or gated work')
  addFactor(factors, 'risk', (record.riskSignals ?? []).length * 8, 'risk signal present')
  addFactor(factors, 'urgency', Math.max(0, Math.min(20, record.urgency ?? 0)), 'urgent operational state')

  const dueInDays = daysUntil(record.dueDateIso, nowIso)
  addFactor(factors, 'due_soon', dueInDays !== null && dueInDays >= -7 && dueInDays <= 14 ? 18 - Math.max(0, dueInDays) : 0, 'near-term due date')

  const updatedDays = daysUntil(record.updatedAtIso, nowIso)
  addFactor(factors, 'recency', updatedDays !== null && updatedDays <= 0 && updatedDays >= -14 ? 10 + updatedDays : 0, 'recent activity')
  addFactor(factors, 'active_status', /in_progress|ongoing|ready|live/i.test(`${record.status ?? ''} ${record.workflowStatus ?? ''}`) ? 10 : 0, 'active workflow state')
  addFactor(factors, 'assignee', request.actorId && (record.assigneeIds ?? []).includes(request.actorId) ? 8 : 0, 'assigned to current actor')
  addFactor(factors, 'activity_density', Number(record.metadata?.activityCount ?? 0) > 1 ? 6 : 0, 'multiple related activity signals')

  const score = factors.reduce((sum, factor) => sum + factor.score, 0)
  const stage = factors.some((factor) => factor.name === 'direct_target')
    ? 'direct_target'
    : factors.some((factor) => factor.name === 'dependency_proximity')
      ? 'dependency'
      : factors.some((factor) => ['blocked', 'risk', 'urgency', 'due_soon', 'active_status'].includes(factor.name))
        ? 'operational_urgency'
        : record.layer === 'strategic_memory'
          ? 'strategic'
          : record.layer === 'historical'
            ? 'historical'
            : 'background'

  return {
    ...record,
    ranking: { score, stage, factors },
  }
}

export const rankRetrievalRecords = (
  records: AiRetrievalRecord[],
  request: AiRetrievalRequest,
): AiRankedRetrievalRecord[] => {
  const seen = new Map<string, AiRankedRetrievalRecord>()
  records.forEach((record) => {
    const ranked = rankRetrievalRecord(record, request)
    const key = `${ranked.scope}:${ranked.id}`
    const existing = seen.get(key)
    if (!existing || ranked.ranking.score > existing.ranking.score) seen.set(key, ranked)
  })
  return [...seen.values()]
    .filter((record) => record.ranking.score > 0 || record.layer === 'immediate_request')
    .sort((left, right) => right.ranking.score - left.ranking.score || left.title.localeCompare(right.title))
}
